import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { TrainingItem, TrainingCompletion } from '../types';

const ITEM_FIELDS = [
  'dagrun_pm_trainingitemid', 'dagrun_pm_name', 'dagrun_pm_trainingtype',
  'dagrun_pm_assigneeid', 'dagrun_pm_assigneename',
  'dagrun_pm_duedate', 'dagrun_pm_status', 'dagrun_pm_notes', 'dagrun_pm_projectid',
  'createdon',
];
const COMP_FIELDS = [
  'dagrun_pm_trainingcompletionid', 'dagrun_pm_name', 'dagrun_pm_trainingitemid',
  'dagrun_pm_completedon', 'dagrun_pm_completedbyid', 'dagrun_pm_completedbyname',
  'dagrun_pm_score', 'dagrun_pm_notes', 'createdon',
];

export async function getProjectTrainingItems(pca: IPublicClientApplication, projectId: string): Promise<TrainingItem[]> {
  return dvList<TrainingItem>(pca, 'dagrun_pm_trainingitems', {
    select: ITEM_FIELDS,
    filter: `dagrun_pm_projectid eq '${projectId}'`,
    orderby: 'createdon desc',
    top: 500,
  });
}

export interface CreateTrainingItemInput {
  projectId: string;
  name: string;
  trainingtype?: number;
  assigneeid?: string | null;
  assigneename?: string | null;
  duedate?: string | null;
  status?: number;
  notes?: string | null;
}

export async function createTrainingItem(pca: IPublicClientApplication, input: CreateTrainingItemInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.name,
    dagrun_pm_projectid: input.projectId,
    dagrun_pm_status: input.status ?? 1, // NotStarted
  };
  if (input.trainingtype != null) body.dagrun_pm_trainingtype = input.trainingtype;
  if (input.assigneeid)           body.dagrun_pm_assigneeid = input.assigneeid;
  if (input.assigneename)         body.dagrun_pm_assigneename = input.assigneename;
  if (input.duedate)              body.dagrun_pm_duedate = input.duedate;
  if (input.notes)                body.dagrun_pm_notes = input.notes;
  return dvCreate(pca, 'dagrun_pm_trainingitems', body);
}

export async function updateTrainingItem(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_trainingitems', id, patch);
}

export async function deleteTrainingItem(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_trainingitems', id);
}

// ── Completions ────────────────────────────────────────────────────

export async function getItemCompletions(pca: IPublicClientApplication, trainingItemId: string): Promise<TrainingCompletion[]> {
  return dvList<TrainingCompletion>(pca, 'dagrun_pm_trainingcompletions', {
    select: COMP_FIELDS,
    filter: `dagrun_pm_trainingitemid eq '${trainingItemId}'`,
    orderby: 'dagrun_pm_completedon desc',
    top: 200,
  });
}

export interface CreateCompletionInput {
  trainingItemId: string;
  completedon: string;          // ISO date
  completedbyid: string;
  completedbyname: string;
  score?: number | null;
  notes?: string | null;
}

export async function createCompletion(pca: IPublicClientApplication, input: CreateCompletionInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: `${input.completedbyname} — ${new Date(input.completedon).toLocaleDateString()}`,
    dagrun_pm_trainingitemid: input.trainingItemId,
    dagrun_pm_completedon: input.completedon,
    dagrun_pm_completedbyid: input.completedbyid,
    dagrun_pm_completedbyname: input.completedbyname,
  };
  if (input.score != null) body.dagrun_pm_score = input.score;
  if (input.notes)         body.dagrun_pm_notes = input.notes;
  return dvCreate(pca, 'dagrun_pm_trainingcompletions', body);
}

export async function deleteCompletion(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_trainingcompletions', id);
}

// Fetch completion counts for a batch of training items. Returns a Map of
// trainingItemId -> completion count. Uses $apply/groupby for O(1) network
// cost regardless of item count.
export async function getCompletionCounts(pca: IPublicClientApplication, projectId: string): Promise<Map<string, number>> {
  const items = await dvList<TrainingCompletion & { _count?: number }>(pca, 'dagrun_pm_trainingcompletions', {
    select: ['dagrun_pm_trainingitemid'],
    // Filter to this project by joining through the training item on the client
    // since trainingitemid here is a plain string not a lookup.
    top: 5000,
  });
  const counts = new Map<string, number>();
  items.forEach(c => {
    const k = c.dagrun_pm_trainingitemid;
    if (!k) return;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  });
  return counts;
  // Note: projectId param is reserved for future server-side filtering if
  // we move to a relationship join. Client-side count is sufficient for the
  // typical training-record volume in any one project.
}
